import { createTravelPost, TravelPost } from '@cubemeet/core/services/travelPostService';
import { SnapshotDay, SnapshotEntry, TravelPostSnapshot } from '../types';

export interface PublishSnapshotParams {
  snapshot: TravelPostSnapshot;
  status?: 'draft' | 'published';
}

const escapeHtml = (input: string) => input
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const renderEntry = (entry: SnapshotEntry) => {
  const parts: string[] = [];
  parts.push(`<h3>${escapeHtml(`${entry.order}. ${entry.title}`)}</h3>`);
  if (entry.summary) {
    parts.push(`<p>${escapeHtml(entry.summary)}</p>`);
  }
  entry.notes.forEach((note) => {
    if (note.text) {
      parts.push(`<p>${escapeHtml(note.text)}</p>`);
    }
  });
  entry.gallery.forEach((image) => {
    if (image.url) {
      parts.push(`<img src="${image.url}" alt="${escapeHtml(image.alt || entry.title)}" />`);
    }
  });
  return parts.join('');
};

const renderDay = (day: SnapshotDay) => {
  const parts: string[] = [];
  const heading = day.title ? `${day.dayNumber}. ${day.title}` : `Day ${day.dayNumber}`;
  parts.push(`<h2>${escapeHtml(heading)}</h2>`);
  day.entries.filter((entry) => !entry.hidden).forEach((entry) => {
    parts.push(renderEntry(entry));
  });
  return parts.join('');
};

export const snapshotToHtml = (snapshot: TravelPostSnapshot) => {
  const parts: string[] = [];
  if (snapshot.title) {
    parts.push(`<h1>${escapeHtml(snapshot.title)}</h1>`);
  }
  if (snapshot.summary) {
    parts.push(`<p>${escapeHtml(snapshot.summary)}</p>`);
  }
  snapshot.days.filter((day) => !day.hidden).forEach((day) => parts.push(renderDay(day)));
  return parts.join('');
};

const deriveSummary = (snapshot: TravelPostSnapshot) => {
  if (snapshot.summary) return snapshot.summary;
  for (const day of snapshot.days) {
    for (const entry of day.entries) {
      if (entry.summary) return entry.summary;
      const firstNote = entry.notes.find((note) => !!note.text);
      if (firstNote?.text) return firstNote.text;
    }
  }
  return '旅遊指南';
};

const deriveCoverImage = (snapshot: TravelPostSnapshot) => {
  if (snapshot.coverImage) return snapshot.coverImage;
  for (const day of snapshot.days) {
    for (const entry of day.entries) {
      if (entry.coverImage?.url) return entry.coverImage.url;
      if (entry.gallery[0]?.url) return entry.gallery[0].url;
    }
  }
  return undefined;
};

export const publishSnapshot = async ({ snapshot, status = 'published' }: PublishSnapshotParams): Promise<TravelPost> => {
  const contentHtml = snapshotToHtml(snapshot);
  const coverImage = deriveCoverImage(snapshot);
  const summary = deriveSummary(snapshot);

  return createTravelPost({
    title: snapshot.title || '未命名指南',
    summary,
    cover_image: coverImage,
    city: snapshot.city,
    tags: snapshot.tags,
    content_json: snapshot,
    content_html: contentHtml,
    status,
    source_type: 'itinerary_snapshot',
    source_itinerary_id: snapshot.meta?.sourceItineraryId,
    snapshot_schema_version: snapshot.schemaVersion,
    snapshot_generated_at: snapshot.generatedAt,
    snapshot_meta: snapshot.meta,
  });
};
